import { Controller } from "@hotwired/stimulus"
import { computeABGroups, toExperienceInput, allGroupsSingleCountry } from "../lib/ab_groups"

export default class extends Controller {
  static targets = ["statusCheckbox", "errorMessage", "statusLabel"]

  connect() {
    setTimeout(() => {
      this.validateRequirements()
      // Ensure checkbox state is correct after initial validation
      if (this.hasStatusCheckboxTarget) {
        const isValid = this.validateRequirements()
        if (isValid) {
          this.enableCheckbox(this.statusCheckboxTarget)
        }
      }
    }, 500)
    this.setupChangeListeners()
    this.setupStatusChangeHandler()
  }

  setupStatusChangeHandler() {
    if (!this.hasStatusCheckboxTarget) return
    
    const checkbox = this.statusCheckboxTarget
    const label = checkbox.closest('label')
    
    // Add click handler to label to ensure clicks work
    if (label) {
      label.addEventListener('click', (e) => {
        // Only process if checkbox is not disabled
        if (checkbox.disabled) {
          e.preventDefault()
          e.stopPropagation()
          return
        }
        
        // If clicking directly on checkbox, let it handle itself
        if (e.target === checkbox) {
          return
        }
        
        // For label clicks, the browser should automatically toggle the checkbox
        // But we'll ensure it happens by checking after the event
        const wasChecked = checkbox.checked
        
        // Use requestAnimationFrame to check after browser processes the click
        requestAnimationFrame(() => {
          const nowChecked = checkbox.checked
          
          // If checkbox didn't toggle and it's not disabled, manually trigger it
          if (wasChecked === nowChecked && !checkbox.disabled) {
            // Manually toggle the checkbox
            checkbox.checked = !wasChecked
            // Dispatch change event manually
            const changeEvent = new Event('change', { bubbles: true, cancelable: true })
            checkbox.dispatchEvent(changeEvent)
          }
        })
      }, false) // Use bubble phase, don't interfere with default label behavior
    }
    
    // Add change handler for label updates (this runs AFTER the Stimulus action)
    checkbox.addEventListener('change', (e) => {
      this.updateStatusLabel(e)
    }, false) // Use bubble phase to catch it after Stimulus
  }

  updateStatusLabel(event) {
    if (!this.hasStatusLabelTarget) return
    
    const label = this.statusLabelTarget
    const turnOnText = label.dataset.turnOnText
    const turnOffText = label.dataset.turnOffText
    
    if (turnOnText && turnOffText) {
      label.textContent = event.target.checked ? turnOffText : turnOnText
    }
    
    // Update message visibility
    const draftMessage = document.getElementById('draft-message')
    const activeMessage = document.getElementById('active-message')
    if (draftMessage) draftMessage.style.display = event.target.checked ? 'none' : 'block'
    if (activeMessage) activeMessage.style.display = event.target.checked ? 'block' : 'none'
  }

  setupChangeListeners() {
    const form = this.getForm()
    if (!form) return

    // General form changes
    form.addEventListener('change', () => this.debounceValidation())
    form.addEventListener('input', () => this.debounceValidation())
    form.addEventListener('tom-select:change', () => this.debounceValidation())

    // Turbo events
    document.addEventListener('turbo:frame-load', (e) => {
      if (e.target.id === 'rules' || e.target.querySelector('[data-controller*="verify-rules"]')) {
        setTimeout(() => this.validateRequirements(), 500)
      }
    })
    document.addEventListener('turbo:load', () => setTimeout(() => this.validateRequirements(), 500))

    // Button clicks that affect validation
    form.addEventListener('click', (e) => {
      const triggers = [
        'button[data-action*="location-selector#saveLocation"]',
        'button[data-action*="rules#saveDevice"]',
        'button[data-action*="visibility-time-range#saveTimeRange"]'
      ]
      if (triggers.some(selector => e.target.closest(selector))) {
        setTimeout(() => this.validateRequirements(), 500)
      }
    })

    // Campaign schedule changes
    this.setupScheduleListeners(form)
  }

  setupScheduleListeners(form) {
    const scheduleInput = this.findCampaignField(form, 'input[name*="[schedule]"]')
    if (scheduleInput) {
      scheduleInput.addEventListener('change', () => this.debounceValidation(500))
      scheduleInput.addEventListener('input', () => this.debounceValidation(500))
    }

    const startAt = this.findCampaignField(form, 'input[name*="[start_at]"]')
    const endAt = this.findCampaignField(form, 'input[name*="[end_at]"]')
    
    if (startAt) startAt.addEventListener('change', () => this.debounceValidation())
    if (endAt) endAt.addEventListener('change', () => this.debounceValidation())
  }

  validateRequirements() {
    const errors = []

    const hasExperience = this.hasAtLeastOneExperience()
    const hasSchedule = this.hasCampaignSchedule()

    if (!hasExperience) {
      errors.push("At least one experience must be added")
    }

    if (!hasSchedule) {
      errors.push("Campaign schedule must be created")
    }

    const abRulesError = this.checkAbRulesRequirement()
    if (abRulesError) errors.push(abRulesError)

    const intruderError = this.checkAbIntruderConfirmation()
    if (intruderError) errors.push(intruderError)

    const isValid = errors.length === 0
    this.updateStatusCheckbox(isValid)
    
    const isCampaignActive = this.hasStatusCheckboxTarget && this.statusCheckboxTarget.checked
    this.displayErrors(isCampaignActive ? errors : [])

    return isValid
  }

  hasAtLeastOneExperience() {
    // Check rules section (most reliable)
    const rulesSection = document.querySelector('[data-controller*="verify-rules"]')
    if (rulesSection) {
      const rows = rulesSection.querySelectorAll('.flex.gap-4')
      const validRows = Array.from(rows).filter(row => 
        !row.querySelector('.text-gray-400') && 
        (row.querySelector('input[name*="[start_at]"]') || 
         row.querySelector('input[name*="[end_at]"]') ||
         row.querySelector('select[name*="[devices]"]') ||
         row.querySelector('input[name*="[id]"]'))
      )
      if (validRows.length > 0) return true
    }

    // Check experiences list section
    const experiencesSection = document.querySelector('#experiences')
    if (experiencesSection) {
      const nestedFields = experiencesSection.querySelectorAll('.nested-fields')
      const visibleFields = Array.from(nestedFields).filter(field => {
        const destroyField = field.querySelector('input[name*="[_destroy]"]')
        return !destroyField || destroyField.value !== '1'
      })
      if (visibleFields.length > 0) return true

      if (experiencesSection.querySelectorAll('[data-controller*="rules"]').length > 0) return true
    }

    // Check for experience/campaign_experience ID fields in form
    const form = this.getForm()
    if (form) {
      const idFields = form.querySelectorAll('input[name*="experiences_attributes"][name*="[id]"], input[name*="campaign_experiences_attributes"][name*="[id]"]')
      const validIds = Array.from(idFields).filter(field => field.value?.trim())
      if (validIds.length > 0) return true
    }

    return false
  }

  hasCampaignSchedule() {
    const form = this.getForm()
    if (!form) return false

    // Check schedule text field
    const scheduleInput = this.findCampaignField(form, 'input[name*="[schedule]"]')
    if (scheduleInput?.value?.trim()) return true

    // Check hidden date fields
    const startAt = this.findCampaignField(form, 'input[name*="[start_at]"]')
    const endAt = this.findCampaignField(form, 'input[name*="[end_at]"]')
    
    return startAt?.value?.trim() && endAt?.value?.trim()
  }

  checkAbIntruderConfirmation() {
    const verifyEl = document.querySelector("[data-controller*=\"verify-rules\"]")
    if (!verifyEl || verifyEl.dataset.abHasIntruders !== "true") return null
    const form = this.getForm()
    if (!form) return null
    const abRulesSelect = form.querySelector("select[name*=\"[ab_rules]\"]")
    if (!abRulesSelect || (abRulesSelect.value || "automatic") !== "automatic") return null
    const checkbox = document.getElementById("ab_intruder_confirm")
    if (!checkbox || checkbox.checked) return null
    return "Changing locations has altered which experiences are A/B tested. Please confirm before saving."
  }

  checkAbRulesRequirement() {
    const form = this.getForm()
    if (!form) return null

    const experiences = this.getExperiences()
    if (!experiences || experiences.length < 2) return null

    const inputs = experiences.map((row) => toExperienceInput(row))
    const abGroups = computeABGroups(inputs)
    if (Object.keys(abGroups).length === 0) return null

    const abRulesSelect = form.querySelector('select[name*="[ab_rules]"]')
    if (!abRulesSelect) return null

    const value = (abRulesSelect.value || "").trim()
    if (value === "day" || value === "time") {
      const expLocations = {}
      experiences.forEach((row) => {
        const countries = row.country_code || []
        const state = row.state
        const region =
          Array.isArray(state) && state.length > 1
            ? `MULTI:${state.join(",")}`
            : Array.isArray(state) && state.length === 1
              ? state[0]
              : state || null
        expLocations[row.id] = { all: countries.length === 0, countries, region }
      })
      if (!allGroupsSingleCountry(abGroups, expLocations)) {
        return "Day-of-week and time-of-day A/B rules are only available when all eligible experiences in each group target the same single country."
      }
    }

    if (abRulesSelect.disabled || !value || value === "automatic") {
      return "A/B rules must be selected (Day of the week or Time of day) when eligible groups exist (experiences overlapping in location, schedule, and device)."
    }

    return null
  }

  getExperiences() {
    const form = this.getForm()
    if (!form) return []

    // Try rules section first
    const rulesSection = form.querySelector('[data-controller*="verify-rules"]') || 
                        document.querySelector('[data-controller*="verify-rules"]')
    
    if (rulesSection) {
      return this.getExperiencesFromRules(rulesSection)
    }

    // Try admin version
    const experiencesSection = form.querySelector('#experiences')
    if (experiencesSection) {
      return this.getAdminExperiences(experiencesSection)
    }

    return []
  }

  getExperiencesFromRules(rulesSection) {
    const rows = rulesSection.querySelectorAll('.flex.gap-4')
    return Array.from(rows)
      .filter(row => !row.querySelector('.text-gray-400'))
      .filter(row => row.querySelector('input[name*="[start_at]"]') || 
                     row.querySelector('input[name*="[end_at]"]') ||
                     row.querySelector('select[name*="[devices]"]'))
      .map((row, index) => ({
        id: this.getExperienceIdFromRow(row) || `row-${index}`,
        name: this.getExperienceNameFromRow(row),
        country_code: this.getCountryCodesFromRow(row),
        state: this.getStateCodesFromRow(row),
        start_at: this.getFieldValue(row, 'input[name*="[start_at]"]'),
        end_at: this.getFieldValue(row, 'input[name*="[end_at]"]'),
        devices: this.getDevicesRawFromRow(row),
        schedule: this.getFieldValue(row, 'input[name*="[schedule]"]'),
        location: this.getLocationFromRow(row)
      }))
  }

  getExperienceIdFromRow(row) {
    const expIdField = row.querySelector('input[name$="[experience_id]"]')
    if (expIdField?.value?.trim()) return expIdField.value.trim()
    const idField = row.querySelector('input[name$="[id]"]')
    return idField?.value?.trim() || ''
  }

  getStateCodesFromRow(row) {
    const select = row.querySelector('select[name*="[state]"]')
    if (!select) return []
    if (select.tomselect) {
      const val = select.tomselect.getValue()
      return Array.isArray(val) ? val.filter((v) => v !== "all") : []
    }
    return Array.from(select.selectedOptions || []).map((o) => o.value).filter((v) => v && v !== "all")
  }

  getExperienceNameFromRow(row) {
    const nameDiv = row.querySelector('.truncate')
    return nameDiv?.textContent?.trim() || 'Experience'
  }

  getCountryCodesFromRow(row) {
    const select = row.querySelector('select[name*="[country_code]"]')
    if (!select) return []
    if (select.tomselect) {
      const val = select.tomselect.getValue()
      return Array.isArray(val) ? val : (val ? [val] : [])
    }
    return Array.from(select.selectedOptions || []).map(o => o.value).filter(Boolean)
  }

  getDevicesRawFromRow(row) {
    const select = row.querySelector('select[name*="[devices]"]')
    if (!select) return 'all'
    if (select.tomselect) return select.tomselect.getValue() || 'all'
    return select.value || 'all'
  }

  getAdminExperiences(experiencesSection) {
    const containers = experiencesSection.querySelectorAll('[data-controller*="rules"]')
    return Array.from(containers).map(container => ({
      schedule: this.getAdminSchedule(container),
      location: this.getAdminLocation(container),
      devices: this.getAdminDevices(container)
    }))
  }

  getLocationFromRow(row) {
    const container = row.querySelector('[data-controller*="location-selector"]')
    if (!container) return 'All Locations'

    const button = container.querySelector('[data-location-selector-target="selectorButton"]')
    const display = container.querySelector('[data-location-selector-target="displayLocation"]')
    
    if (button && !button.classList.contains('hidden') && 
        display && display.classList.contains('hidden')) {
      return 'All Locations'
    }

    const textElement = container.querySelector('[data-location-selector-target="locationText"]')
    if (textElement) {
      const parentDisplay = textElement.closest('[data-location-selector-target="displayLocation"]')
      if (parentDisplay && !parentDisplay.classList.contains('hidden')) {
        return textElement.textContent?.trim() || 'All Locations'
      }
    }

    return 'All Locations'
  }

  getDevicesFromRow(row) {
    const select = row.querySelector('select[name*="[devices]"]')
    if (!select) return ''

    const option = select.options[select.selectedIndex]
    if (option) return option.textContent?.trim() || ''

    const value = select.value
    if (value === 'all') return 'All devices'
    if (value === 'IOS') return 'IOS'
    if (value === 'Android') return 'Android'
    return value || ''
  }

  getAdminSchedule(container) {
    const dateSelect = container.querySelector('[data-rules-target="dateSelect"]')
    if (dateSelect?.dataset.persisted === 'true') {
      const start = dateSelect.dataset.startValue || ''
      const end = dateSelect.dataset.endValue || ''
      if (start && end) return `From ${start} to ${end}`
    }

    const startAt = container.querySelector('input[name*="[start_at]"]')
    const endAt = container.querySelector('input[name*="[end_at]"]')
    if (startAt?.value && endAt?.value) {
      return `From ${startAt.value} to ${endAt.value}`
    }

    return ''
  }

  getAdminLocation(container) {
    const locationContainer = container.querySelector('[data-controller*="location-selector"]')
    if (!locationContainer) return 'All Locations'

    const button = locationContainer.querySelector('[data-location-selector-target="selectorButton"]')
    const display = locationContainer.querySelector('[data-location-selector-target="displayLocation"]')
    
    if (button && !button.classList.contains('hidden') && 
        display && display.classList.contains('hidden')) {
      return 'All Locations'
    }

    const textElement = locationContainer.querySelector('[data-location-selector-target="locationText"]')
    if (textElement) {
      const parentDisplay = textElement.closest('[data-location-selector-target="displayLocation"]')
      if (parentDisplay && !parentDisplay.classList.contains('hidden')) {
        return textElement.textContent?.trim() || 'All Locations'
      }
    }

    return 'All Locations'
  }

  getAdminDevices(container) {
    const deviceText = container.querySelector('[data-rules-target="deviceText"]')
    if (deviceText) {
      const displayDevice = deviceText.closest('[data-rules-target="displayDevice"]')
      if (displayDevice && !displayDevice.classList.contains('hidden')) {
        return deviceText.textContent?.trim() || ''
      }
    }

    const select = container.querySelector('select[name*="[devices]"]')
    if (select) {
      if (select.value === 'IOS') return 'IOS'
      if (select.value === 'Android') return 'Android'
      return select.value || ''
    }

    return ''
  }

  hasOverlappingExperiences(experiences) {
    for (let i = 0; i < experiences.length; i++) {
      for (let j = i + 1; j < experiences.length; j++) {
        if (this.experiencesOverlap(experiences[i], experiences[j])) return true
      }
    }
    return false
  }

  experiencesOverlap(expA, expB) {
    return this.locationOverlap(expA, expB) &&
      this.scheduleOverlap(expA, expB) &&
      this.deviceOverlap(expA, expB)
  }

  locationOverlap(expA, expB) {
    const codesA = expA.country_code || []
    const codesB = expB.country_code || []
    if (codesA.length === 0 || codesB.length === 0) return true
    return codesA.some(c => codesB.includes(c))
  }

  scheduleOverlap(expA, expB) {
    const startA = expA.start_at ? new Date(expA.start_at).getTime() : 0
    const endA = expA.end_at ? new Date(expA.end_at).getTime() : Infinity
    const startB = expB.start_at ? new Date(expB.start_at).getTime() : 0
    const endB = expB.end_at ? new Date(expB.end_at).getTime() : Infinity
    return startA <= endB && startB <= endA
  }

  deviceOverlap(expA, expB) {
    const devA = this.normalizeDevice(expA.devices)
    const devB = this.normalizeDevice(expB.devices)
    return devA === 'all' || devB === 'all' || devA === devB
  }

  normalizeDevice(devices) {
    if (!devices || devices === 'All devices') return 'all'
    if (typeof devices === 'string' && devices.toLowerCase() === 'all') return 'all'
    return devices
  }

  updateStatusCheckbox(isValid) {
    if (!this.hasStatusCheckboxTarget) return

    const checkbox = this.statusCheckboxTarget
    const wasChecked = checkbox.checked

    if (!isValid && !wasChecked) {
      // Disable if invalid and not checked
      this.disableCheckbox(checkbox)
    } else if (isValid) {
      // Always enable when valid, regardless of current checked state
      this.enableCheckbox(checkbox)
    }

    // If validation fails and checkbox is checked, uncheck it
    if (!isValid && wasChecked) {
      checkbox.checked = false
      // Trigger change event to update UI
      const changeEvent = new Event('change', { bubbles: true, cancelable: true })
      checkbox.dispatchEvent(changeEvent)
    }
  }

  disableCheckbox(checkbox) {
    checkbox.disabled = true
    checkbox.setAttribute('disabled', 'disabled')
    checkbox.style.opacity = '0.5'
    checkbox.style.cursor = 'not-allowed'
    checkbox.style.pointerEvents = 'none'
    
    const label = checkbox.closest('label')
    if (label) {
      label.style.cursor = 'not-allowed'
      label.style.opacity = '0.5'
      label.style.pointerEvents = 'none'
    }
  }

  enableCheckbox(checkbox) {
    checkbox.disabled = false
    checkbox.removeAttribute('disabled')
    checkbox.style.opacity = '1'
    checkbox.style.cursor = 'pointer'
    checkbox.style.pointerEvents = 'auto'
    
    const label = checkbox.closest('label')
    if (label) {
      label.style.cursor = 'pointer'
      label.style.opacity = '1'
      label.style.pointerEvents = 'auto'
      label.classList.remove('pointer-events-none')
    }
    
    // Ensure all parent containers allow pointer events
    let container = checkbox.closest('.flex.items-center.gap-3')
    if (container) {
      container.classList.remove('pointer-events-none')
      container.style.pointerEvents = 'auto'
      container.style.opacity = '1'
    }
    
    // Also check the immediate parent
    container = checkbox.closest('.flex.items-center')
    if (container && container !== checkbox.closest('.flex.items-center.gap-3')) {
      container.classList.remove('pointer-events-none')
      container.style.pointerEvents = 'auto'
    }
  }

  displayErrors(errors) {
    if (!this.hasErrorMessageTarget) {
      this.createErrorMessageContainer()
    }
    if (!this.hasErrorMessageTarget) return

    const container = this.errorMessageTarget
    if (errors.length > 0) {
      const errorList = errors.map(e => `<li class="text-xs">${e}</li>`).join('')
      container.innerHTML = `
        <div class="bg-red-900/20 border border-red-500 rounded-md p-3 mb-4">
          <p class="text-red-400 font-semibold text-xs mb-2">Campaign cannot be turned on:</p>
          <ul class="list-disc list-inside text-red-300 space-y-1">${errorList}</ul>
        </div>
      `
      container.classList.remove('hidden')
    } else {
      container.classList.add('hidden')
      container.innerHTML = ''
    }
  }

  createErrorMessageContainer() {
    const form = this.getForm()
    if (!form) return

    const statusContainer = form.querySelector('input[name*="[status]"]')?.closest('.flex.items-center')
    if (!statusContainer) return

    const errorContainer = document.createElement('div')
    errorContainer.setAttribute('data-campaign-validation-target', 'errorMessage')
    errorContainer.className = 'hidden w-full mt-2'
    
    const parent = statusContainer.parentElement
    if (parent) {
      parent.insertAdjacentElement('afterend', errorContainer)
    }
  }

  handleStatusChange(event) {
    // If checkbox is disabled, don't process
    if (this.statusCheckboxTarget.disabled) {
      event.preventDefault()
      event.stopPropagation()
      return false
    }

    // Validate requirements
    const isValid = this.validateRequirements()
    
    // Only prevent turning ON if validation fails
    if (!isValid && event.target.checked) {
      // Prevent the change
      event.preventDefault()
      event.stopPropagation()
      event.stopImmediatePropagation()
      
      // Force unchecked state
      event.target.checked = false
      
      // Update visual state
      this.updateStatusLabel(event)
      
      // Show errors
      const errors = []
      if (!this.hasAtLeastOneExperience()) errors.push("At least one experience must be added")
      if (!this.hasCampaignSchedule()) errors.push("Campaign schedule must be created")
      const abRulesError = this.checkAbRulesRequirement()
      if (abRulesError) errors.push(abRulesError)
      
      this.displayErrors(errors)
      return false
    }
    
    // If turning OFF, clear errors
    if (!event.target.checked) {
      this.displayErrors([])
    }
    
    // Update label text
    this.updateStatusLabel(event)
    
    return true
  }

  // Helper methods
  getForm() {
    return this.element.querySelector('form')
  }

  findCampaignField(form, selector) {
    const allFields = form.querySelectorAll(selector)
    return Array.from(allFields).find(field => {
      const name = field.name || ''
      return name.includes(selector.includes('[schedule]') ? '[schedule]' : 
                           selector.includes('[start_at]') ? '[start_at]' : '[end_at]') &&
             !name.includes('experiences_attributes') && !name.includes('campaign_experiences_attributes')
    })
  }

  getFieldValue(container, selector) {
    const field = container.querySelector(selector)
    return field?.value?.trim() || ''
  }

  debounceValidation(delay = 300) {
    clearTimeout(this.validationTimeout)
    this.validationTimeout = setTimeout(() => this.validateRequirements(), delay)
  }
}
